/**
 * 工单管理 - 常量定义
 */

// 工单状态
export const ORDER_STATUS = {
  RECTIFYING: 'rectifying', // 待整改
  REVIEWING: 'reviewing', // 待审核
  APPROVED: 'approved', // 已通过
  REJECTED: 'rejected', // 已驳回
  APPEAL_REVIEWING: 'appeal_reviewing' // 待复审
} as const

export type OrderStatusType = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

// 状态显示配置
export const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string }
> = {
  [ORDER_STATUS.RECTIFYING]: {
    label: '待整改',
    color: '#FA8C16',
    bgColor: '#FFF7E6'
  },
  [ORDER_STATUS.REVIEWING]: {
    label: '待审核',
    color: '#1890FF',
    bgColor: '#E6F7FF'
  },
  [ORDER_STATUS.APPROVED]: {
    label: '已通过',
    color: '#52C41A',
    bgColor: '#F6FFED'
  },
  [ORDER_STATUS.REJECTED]: {
    label: '已驳回',
    color: '#F5222D',
    bgColor: '#FFF1F0'
  },
  [ORDER_STATUS.APPEAL_REVIEWING]: {
    label: '待复审',
    color: '#722ED1',
    bgColor: '#F9F0FF'
  }
}

// 分析结果
export const ANALYSIS_RESULT = {
  COMPLIANT: 'compliant', // 符合
  NON_COMPLIANT: 'non_compliant', // 不符合
  PENDING: 'pending' // 待分析
} as const

export const ANALYSIS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string }
> = {
  [ANALYSIS_RESULT.COMPLIANT]: {
    label: '符合',
    color: '#52C41A',
    bgColor: '#F6FFED'
  },
  [ANALYSIS_RESULT.NON_COMPLIANT]: {
    label: '不符合',
    color: '#F5222D',
    bgColor: '#FFF1F0'
  },
  [ANALYSIS_RESULT.PENDING]: {
    label: '待分析',
    color: '#FA8C16',
    bgColor: '#FFF7E6'
  }
}

// API 返回的中文 tag_value → 前端英文 enum 映射
export const TAG_VALUE_API_MAP: Record<string, string> = {
  符合: ANALYSIS_RESULT.COMPLIANT,
  不符合: ANALYSIS_RESULT.NON_COMPLIANT,
  待分析: ANALYSIS_RESULT.PENDING
}

export function mapTagValue(apiValue: string): string {
  return TAG_VALUE_API_MAP[apiValue] || apiValue
}

// 前端英文 enum → API 中文 tag_value（筛选传参用）
export const TAG_VALUE_REVERSE_MAP: Record<string, string> = {
  [ANALYSIS_RESULT.COMPLIANT]: '符合',
  [ANALYSIS_RESULT.NON_COMPLIANT]: '不符合',
  [ANALYSIS_RESULT.PENDING]: '待分析'
}

export function reverseTagValue(enumValue: string): string {
  return TAG_VALUE_REVERSE_MAP[enumValue] || enumValue
}

// 工单状态筛选选项
export const STATUS_FILTER_OPTIONS = [
  { label: '全部状态', value: '' },
  { label: '待整改', value: ORDER_STATUS.RECTIFYING },
  { label: '待审核', value: ORDER_STATUS.REVIEWING },
  { label: '已通过', value: ORDER_STATUS.APPROVED },
  { label: '已驳回', value: ORDER_STATUS.REJECTED },
  { label: '待复审', value: ORDER_STATUS.APPEAL_REVIEWING }
]

// 分析结果筛选选项
export const ANALYSIS_FILTER_OPTIONS = [
  { label: '全部结果', value: '' },
  { label: '符合', value: ANALYSIS_RESULT.COMPLIANT },
  { label: '不符合', value: ANALYSIS_RESULT.NON_COMPLIANT },
  { label: '待分析', value: ANALYSIS_RESULT.PENDING }
]

// 操作日志类型
export const ACTION_TYPE_CONFIG: Record<
  string,
  { label: string; color: string; icon: string }
> = {
  created: { label: '创建整改', color: '#8C8C8C', icon: 'file-add' },
  submitted: { label: '提交整改', color: '#1890FF', icon: 'upload' },
  approved: { label: '通过', color: '#52C41A', icon: 'check-circle' },
  rejected: { label: '驳回', color: '#F5222D', icon: 'close-circle' },
  reopened: { label: '重新提交', color: '#1890FF', icon: 'redo' },
  appealed: { label: '发起复议', color: '#722ED1', icon: 'exclamation-circle' },
  appeal_approved: {
    label: '通过（复议）',
    color: '#52C41A',
    icon: 'check-circle'
  },
  appeal_rejected: {
    label: '驳回（复议）',
    color: '#F5222D',
    icon: 'close-circle'
  },
  auto_approved: { label: '自动通过', color: '#8C8C8C', icon: 'clock-circle' },
  auto_submitted: { label: '自动提交', color: '#8C8C8C', icon: 'clock-circle' },
  revoked: { label: '撤销通过', color: '#FA8C16', icon: 'rollback' },
  deleted: { label: '删除工单', color: '#F5222D', icon: 'delete' }
}

// ============================================================
// 列表项（checkpoint_list 返回）
// ============================================================
export interface CheckpointRecord {
  checkpoint_key: string
  checkpoint_name: string
  machine_id: string
  machine_model: string
  process_name: string
  site_name: string
  group_id: string
  media_count: number
  image_count: number
  video_count: number
  latest_media_at: string
  status: string // "pending" | "analyzed"
  tag_value: string // 中文: "符合"/"不符合"/"待分析"，经 mapTagValue 转英文
  reason: string
  trigger_type: string
  analyzed_at: string
  order_id: number | null
  order_status: string | null
}

// 兼容旧代码中 OrderRecord 引用
export type OrderRecord = CheckpointRecord

// ============================================================
// 详情（checkpoint_detail 返回）- 嵌套结构
// ============================================================
export interface CheckpointDetailResponse {
  checkpoint_key: string
  checkpoint_name?: string
  site_name?: string
  media_list: MediaItem[]
  result: CheckpointResult | null
  result_history: CheckpointResult[]
  work_order: WorkOrder | null
  logs: ActionLog[]
  can_operate: boolean
  user_ids: string[]
  user_permissions?: string[]
  is_analyzing?: boolean
  analyzing_label?: string
}

export interface CheckpointResult {
  checkpoint_name: string
  machine_id: string
  machine_model: string
  process_name: string
  site_name?: string
  tag_value: string
  trigger_type: string
  analyzed_at: string
  image_count: number
  required_images: number
  video_count: number
  required_videos: number
  reason: string
  label_info: string | LabelInfo | null // API 返回 JSON 字符串，需解析
}

export interface WorkOrder {
  id?: number
  status: string
  status_label?: string
}

// 兼容旧代码中 OrderDetail 引用
export type OrderDetail = CheckpointDetailResponse

export interface LabelInfo {
  objects: Array<{ name: string; value: string }>
  rules: Array<{
    name: string
    passed: boolean
    reason?: string
  }>
  standard?: string[]
}

export interface MediaItem {
  id: string
  photo_seq: number
  file_url: string
  media_type: number // 0=image, 1=video
  checkpoint_name?: string
  machine_id?: string
  machine_model?: string
  process_name?: string
  site_name?: string
}

export interface ActionLog {
  id: string
  action: string
  from_status: string
  to_status: string
  operator_id: string
  operator_role: string
  created_at: string
  comment?: string
}

// ============================================================
// 统计（checkpoint_stats 返回）
// ============================================================
export interface CheckpointStats {
  total: number
  compliant: number
  non_compliant: number
  pending: number
  compliance_rate: number
}

// 兼容旧代码中 OrderStats 引用
export type OrderStats = CheckpointStats

// ============================================================
// 模拟拍照
// ============================================================

export interface SimulateResult {
  status: string // "waiting" | "accepted"
  media: {
    photo_seq: number
    file_url: string
    checkpoint_key: string
    media_type: number
  }
  checkpoint: {
    checkpoint_key: string
    checkpoint_name: string
    machine_id: string
    process_name: string
    image_count: number
    video_count: number
    required_images: number
    required_videos: number
  }
}

export interface TrackerCheckpoint {
  checkpoint_key: string
  checkpoint_name: string
  machine_id: string
  machine_model: string
  site_name: string
  stage: string
  current_images: number
  required_images: number
  current_videos: number
  required_videos: number
  tag_value: string | null
  reason: string | null
  work_order_id: number | null
  work_order_status: string | null
  last_activity: string
}

export interface NewResultItem {
  checkpoint_key: string
  tag_value: string
  reason: string
  created_at: string
  trigger_type: string
}

// ============================================================
// 知识库
// ============================================================

export interface KbEntry {
  id: number
  checkpoint_name: string
  applicable_models: string
  required_images: number
  required_videos: number
  standard: string
  sub_count: number
  rule_count: number
}

export interface KbSub {
  name: string
  media: string
  count: string
  objects: Array<{ name: string; value: string; frame: string }>
  rules: Array<{ name: string; desc: string; res: string; reason: string }>
}

export interface KbDetail extends KbEntry {
  xml_content: string
  parsed: {
    subs: KbSub[]
  }
}

export interface KbStats {
  total: number
  rule_total: number
}
